<template>
  <div class="knowledge-point-admin">
    <div class="course-filter-bar">
      <label>选择课程 <span class="required-mark">*</span></label>
      <select v-model="selectedCourseId" @change="onCourseChange">
        <option :value="null">请选择课程</option>
        <option v-for="course in courses" :key="course.kcId" :value="course.kcId">
          {{ course.name }}（{{ course.category }}）
        </option>
      </select>
      <span v-if="selectedCourseId" class="course-filter-hint">以下为该课程的知识点，前/后向关系仅限本课程内</span>
    </div>

    <div v-if="!selectedCourseId" class="empty-course-hint">
      <p class="empty-course-hint__title">请先选择课程</p>
      <p class="empty-course-hint__desc">在上方下拉框中选择一门课程后，方可创建、编辑或查看知识点。</p>
    </div>

    <template v-else>
      <div class="admin-toolbar admin-toolbar--end">
        <span class="selected-course-label">当前课程：{{ selectedCourse.name }}</span>
        <button class="btn-primary" @click="openCreateModal">创建新知识点</button>
      </div>

      <div class="point-list">
      <div class="search-bar">
        <input 
          type="text" 
          v-model="searchKeyword" 
          placeholder="查找知识点..."
          @input="handleSearch"
        >
      </div>
      
      <table class="point-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>名称</th>
            <th>分类</th>
            <th>等级</th>
            <th>描述</th>
            <th>前向知识点</th>
            <th>后向知识点</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="point in knowledgePoints" :key="point.kpId">
            <td>{{ point.kpId }}</td>
            <td>{{ point.name }}</td>
            <td>{{ point.category }}</td>
            <td>{{ getLevelText(point.level) }}</td>
            <td>{{ truncateDescription(point.description) }}</td>
            <td>{{ getPrerequisitesText(point.kpId) }}</td>
            <td>{{ getDependentsText(point.kpId) }}</td>
            <td>{{ formatTime(point.createdAt) }}</td>
            <td>
              <button class="btn-view" @click="viewPoint(point)">查看</button>
              <button class="btn-edit" @click="editPoint(point)">编辑</button>
              <button class="btn-delete" @click="deletePoint(point)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>

      <AdminPagination
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="[20, 50, 100]"
        :total="total"
        @update:current-page="changePage"
        @size-change="handlePageSizeChange"
      />
      </div>
    </template>

    <!-- Create/Edit Modal -->
    <div class="modal" v-if="showCreateModal || showEditModal">
      <div class="modal-content">
        <h3>{{ showEditModal ? '编辑知识点' : '创建知识点' }}</h3>
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label>名称 *</label>
            <input type="text" v-model="formData.name" required>
          </div>
          
          <div class="form-group">
            <label>分类 *</label>
            <input type="text" v-model="formData.category" required>
          </div>
          
          <div class="form-group">
            <label>等级 *</label>
            <select v-model="formData.level" required>
              <option value="1">基础</option>
              <option value="2">进阶</option>
              <option value="3">高阶</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>描述</label>
            <textarea v-model="formData.description" rows="4"></textarea>
          </div>

          <!-- 前向知识点选择 -->
          <div class="form-group">
            <label>前向知识点（前置条件）</label>
            <div class="multi-select-container">
              <div class="selected-items">
                <span 
                  v-for="prereq in selectedPrerequisites" 
                  :key="prereq.kpId"
                  class="selected-item"
                >
                  {{ prereq.name }}
                  <button type="button" @click="removePrerequisite(prereq)" class="remove-btn">×</button>
                </span>
              </div>
              <div class="select-dropdown">
                <input 
                  type="text" 
                  v-model="prerequisiteSearch" 
                  placeholder="查找前向知识点..."
                  @input="filterPrerequisites"
                  @focus="showPrerequisiteDropdown = true"
                >
                <div v-if="showPrerequisiteDropdown && filteredPrerequisites.length > 0" class="dropdown-list">
                  <div 
                    v-for="point in filteredPrerequisites" 
                    :key="point.kpId"
                    class="dropdown-item"
                    @click="addPrerequisite(point)"
                  >
                    {{ point.name }} ({{ point.category }})
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 后向知识点选择 -->
          <div class="form-group">
            <label>后向知识点（依赖此知识点的知识点）</label>
            <div class="multi-select-container">
              <div class="selected-items">
                <span 
                  v-for="dependent in selectedDependents" 
                  :key="dependent.kpId"
                  class="selected-item"
                >
                  {{ dependent.name }}
                  <button type="button" @click="removeDependent(dependent)" class="remove-btn">×</button>
                </span>
              </div>
              <div class="select-dropdown">
                <input 
                  type="text" 
                  v-model="dependentSearch" 
                  placeholder="查找后向知识点..."
                  @input="filterDependents"
                  @focus="showDependentDropdown = true"
                >
                <div v-if="showDependentDropdown && filteredDependents.length > 0" class="dropdown-list">
                  <div 
                    v-for="point in filteredDependents" 
                    :key="point.kpId"
                    class="dropdown-item"
                    @click="addDependent(point)"
                  >
                    {{ point.name }} ({{ point.category }})
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn-primary">
              {{ showEditModal ? '更新' : '创建' }}
            </button>
            <button type="button" class="btn-secondary" @click="closeModal">取消</button>
          </div>
        </form>
      </div>
    </div>

    <!-- View Modal -->
    <div class="modal" v-if="showViewModal">
      <div class="modal-content">
        <h3>知识点详情</h3>
        <div class="point-details">
          <div class="detail-item">
            <label>ID:</label>
            <span>{{ selectedPoint.kpId }}</span>
          </div>
          <div class="detail-item">
            <label>名称:</label>
            <span>{{ selectedPoint.name }}</span>
          </div>
          <div class="detail-item">
            <label>分类:</label>
            <span>{{ selectedPoint.category }}</span>
          </div>
          <div class="detail-item">
            <label>等级:</label>
            <span>{{ getLevelText(selectedPoint.level) }}</span>
          </div>
          <div class="detail-item">
            <label>描述:</label>
            <p>{{ selectedPoint.description || '暂无描述' }}</p>
          </div>
          <div class="detail-item">
            <label>前向知识点:</label>
            <div class="relation-list">
              <span v-if="selectedPointPrerequisites.length === 0" class="no-relation">无</span>
              <span 
                v-for="prereq in selectedPointPrerequisites" 
                :key="prereq.kpId"
                class="relation-item"
              >
                {{ prereq.name }} ({{ prereq.category }})
              </span>
            </div>
          </div>
          <div class="detail-item">
            <label>后向知识点:</label>
            <div class="relation-list">
              <span v-if="selectedPointDependents.length === 0" class="no-relation">无</span>
              <span 
                v-for="dependent in selectedPointDependents" 
                :key="dependent.kpId"
                class="relation-item"
              >
                {{ dependent.name }} ({{ dependent.category }})
              </span>
            </div>
          </div>
          <div class="detail-item">
            <label>创建时间:</label>
            <span>{{ formatTime(selectedPoint.createdAt) }}</span>
          </div>
          <div class="detail-item">
            <label>更新时间:</label>
            <span>{{ formatTime(selectedPoint.updatedAt) }}</span>
          </div>
        </div>
        <div class="form-actions">
          <button type="button" class="btn-secondary" @click="closeViewModal">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdminPagination from '@/components/AdminPagination.vue'

export default {
  name: 'KnowledgePointAdmin',
  components: { AdminPagination },
  props: {
    creatorMode: { type: Boolean, default: false },
    initialKcId: { type: [Number, String], default: null }
  },
  data() {
    return {
      courses: [],
      selectedCourseId: null,
      knowledgePoints: [],
      allKnowledgePoints: [],
      currentPage: 1,
      pageSize: 50,
      total: 0,
      totalPages: 1,
      searchKeyword: '',
      showCreateModal: false,
      showEditModal: false,
      showViewModal: false,
      formData: {
        kcId: 0,
        name: '',
        category: '',
        level: 1,
        description: ''
      },
      selectedPoint: {},
      
      // 前向知识点相关
      selectedPrerequisites: [],
      prerequisiteSearch: '',
      showPrerequisiteDropdown: false,
      filteredPrerequisites: [],
      
      // 后向知识点相关
      selectedDependents: [],
      dependentSearch: '',
      showDependentDropdown: false,
      filteredDependents: [],
      
      // 查看模态框中的关系数据
      selectedPointPrerequisites: [],
      selectedPointDependents: [],
      
      // 所有知识点关系数据
      allPrerequisites: [],
      allDependents: []
    }
  },
  computed: {
    selectedCourse() {
      return this.courses.find(c => c.kcId === this.selectedCourseId) || null
    },
    courseScopedPoints() {
      if (!this.selectedCourseId) return []
      return this.allKnowledgePoints.filter(kp => kp.kcId === this.selectedCourseId)
    }
  },
  created() {
    this.fetchCourses()
    this.fetchAllPrerequisites()
  },
  mounted() {
    // 添加点击外部关闭下拉框的事件监听
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    // 移除事件监听
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    async fetchCourses() {
      try {
        const token = localStorage.getItem('jwt_token')
        if (this.creatorMode) {
          const response = await fetch('/api/v1/creator/knowledge/courses/list', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ page: 1, pageSize: 1000, keyword: '' })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.courses = data.data?.list || []
          }
        } else {
          const response = await fetch('/api/v1/knowledge/courses/list', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ page: 1, pageSize: 1000, keyword: '' })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.courses = data.data.list || []
          }
        }
        this.applyInitialCourse()
      } catch (error) {
        console.error('Failed to fetch courses:', error)
      }
    },
    applyInitialCourse() {
      if (!this.initialKcId || this.selectedCourseId) return
      const kcId = Number(this.initialKcId)
      if (this.courses.some(c => c.kcId === kcId)) {
        this.selectedCourseId = kcId
        this.onCourseChange()
      }
    },
    onCourseChange() {
      this.currentPage = 1
      this.searchKeyword = ''
      this.knowledgePoints = []
      this.allKnowledgePoints = []
      this.total = 0
      if (!this.selectedCourseId) return
      this.fetchKnowledgePoints()
      this.fetchAllKnowledgePoints()
    },
    openCreateModal() {
      if (!this.selectedCourseId) {
        this.$message.warning('请先选择课程')
        return
      }
      this.formData = {
        kcId: this.selectedCourseId,
        name: '',
        category: this.selectedCourse?.category || '',
        level: this.selectedCourse?.level || 1,
        description: ''
      }
      this.selectedPrerequisites = []
      this.selectedDependents = []
      this.showCreateModal = true
    },
    async fetchKnowledgePoints() {
      if (!this.selectedCourseId) {
        this.knowledgePoints = []
        this.total = 0
        return
      }
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/points/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            kcId: this.selectedCourseId,
            page: this.currentPage,
            pageSize: this.pageSize,
            keyword: this.searchKeyword
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.knowledgePoints = data.data.list
          this.total = data.data.total
          this.totalPages = Math.ceil(data.data.total / this.pageSize) || 1
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
        } else {
          this.$message.error(data.message || '获取知识点列表失败')
        }
      } catch (error) {
        console.error('Failed to fetch knowledge points:', error)
        this.$message.error('获取知识点列表失败')
      }
    },
    
    async fetchAllKnowledgePoints() {
      if (!this.selectedCourseId) {
        this.allKnowledgePoints = []
        return
      }
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/points/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            kcId: this.selectedCourseId,
            page: 1,
            pageSize: 1000,
            keyword: ''
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.allKnowledgePoints = data.data.list
          // 初始化过滤列表
          this.filterPrerequisites()
          this.filterDependents()
        }
      } catch (error) {
        console.error('Failed to fetch all knowledge points:', error)
      }
    },
    
    async fetchAllPrerequisites() {
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/prerequisites/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            page: 1,
            pageSize: 1000
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.allPrerequisites = data.data.list
        } else {
          console.error('Failed to fetch prerequisites:', data.message)
        }
      } catch (error) {
        console.error('Failed to fetch prerequisites:', error)
      }
    },
    
    handleSearch() {
      this.currentPage = 1
      this.fetchKnowledgePoints()
    },
    changePage(page) {
      this.currentPage = page
      this.fetchKnowledgePoints()
    },
    handlePageSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.fetchKnowledgePoints()
    },
    
    viewPoint(point) {
      if (!this.selectedCourseId) return
      this.selectedPoint = { ...point }
      this.loadPointRelations(point.kpId)
      this.showViewModal = true
    },
    
    async loadPointRelations(kpId) {
      // 加载知识点的前向和后向关系
      try {
        // 获取前向知识点（前置条件）
        const prereqs = this.allPrerequisites.filter(p => p.childKpId === kpId)
        this.selectedPointPrerequisites = prereqs.map(p => {
          const point = this.allKnowledgePoints.find(kp => kp.kpId === p.parentKpId)
          return point || { kpId: p.parentKpId, name: '未知知识点', category: '未知' }
        })
        
        // 获取后向知识点（依赖此知识点的知识点）
        const deps = this.allPrerequisites.filter(p => p.parentKpId === kpId)
        this.selectedPointDependents = deps.map(p => {
          const point = this.allKnowledgePoints.find(kp => kp.kpId === p.childKpId)
          return point || { kpId: p.childKpId, name: '未知知识点', category: '未知' }
        })
      } catch (error) {
        console.error('Failed to load point relations:', error)
        this.selectedPointPrerequisites = []
        this.selectedPointDependents = []
      }
    },
    
    editPoint(point) {
      if (!this.selectedCourseId) return
      this.formData = { ...point }
      this.loadPointRelationsForEdit(point.kpId)
      this.showEditModal = true
    },
    
    async loadPointRelationsForEdit(kpId) {
      // 加载编辑时的关系数据
      try {
        // 获取前向知识点（前置条件）
        const prereqs = this.allPrerequisites.filter(p => p.childKpId === kpId)
        this.selectedPrerequisites = prereqs.map(p => {
          const point = this.allKnowledgePoints.find(kp => kp.kpId === p.parentKpId)
          return point || { kpId: p.parentKpId, name: '未知知识点', category: '未知' }
        })
        
        // 获取后向知识点（依赖此知识点的知识点）
        const deps = this.allPrerequisites.filter(p => p.parentKpId === kpId)
        this.selectedDependents = deps.map(p => {
          const point = this.allKnowledgePoints.find(kp => kp.kpId === p.childKpId)
          return point || { kpId: p.childKpId, name: '未知知识点', category: '未知' }
        })
        
        // 更新过滤后的列表
        this.filterPrerequisites()
        this.filterDependents()
      } catch (error) {
        console.error('Failed to load point relations for edit:', error)
        this.selectedPrerequisites = []
        this.selectedDependents = []
      }
    },
    
    // 前向知识点相关方法
    filterPrerequisites() {
      const pool = this.courseScopedPoints
      if (!this.prerequisiteSearch.trim()) {
        this.filteredPrerequisites = pool.filter(point =>
          !this.selectedPrerequisites.some(selected => selected.kpId === point.kpId)
        )
      } else {
        this.filteredPrerequisites = pool.filter(point =>
          !this.selectedPrerequisites.some(selected => selected.kpId === point.kpId) &&
          (point.name.toLowerCase().includes(this.prerequisiteSearch.toLowerCase()) ||
           point.category.toLowerCase().includes(this.prerequisiteSearch.toLowerCase()))
        )
      }
    },
    
    addPrerequisite(point) {
      this.selectedPrerequisites.push(point)
      this.prerequisiteSearch = ''
      this.showPrerequisiteDropdown = false
      this.filterPrerequisites()
    },
    
    removePrerequisite(point) {
      this.selectedPrerequisites = this.selectedPrerequisites.filter(p => p.kpId !== point.kpId)
      this.filterPrerequisites()
    },
    
    // 后向知识点相关方法
    filterDependents() {
      const pool = this.courseScopedPoints
      if (!this.dependentSearch.trim()) {
        this.filteredDependents = pool.filter(point =>
          !this.selectedDependents.some(selected => selected.kpId === point.kpId)
        )
      } else {
        this.filteredDependents = pool.filter(point =>
          !this.selectedDependents.some(selected => selected.kpId === point.kpId) &&
          (point.name.toLowerCase().includes(this.dependentSearch.toLowerCase()) ||
           point.category.toLowerCase().includes(this.dependentSearch.toLowerCase()))
        )
      }
    },
    
    addDependent(point) {
      this.selectedDependents.push(point)
      this.dependentSearch = ''
      this.showDependentDropdown = false
      this.filterDependents()
    },
    
    removeDependent(point) {
      this.selectedDependents = this.selectedDependents.filter(p => p.kpId !== point.kpId)
      this.filterDependents()
    },
    
    // 获取前向知识点文本
    getPrerequisitesText(kpId) {
      const prereqs = this.allPrerequisites.filter(p => p.childKpId === kpId)
      if (prereqs.length === 0) return '无'
      const prereqNames = prereqs.map(p => {
        const point = this.allKnowledgePoints.find(kp => kp.kpId === p.parentKpId)
        return point ? point.name : '未知'
      })
      return prereqNames.join(', ')
    },
    
    // 获取后向知识点文本
    getDependentsText(kpId) {
      const deps = this.allPrerequisites.filter(p => p.parentKpId === kpId)
      if (deps.length === 0) return '无'
      const depNames = deps.map(p => {
        const point = this.allKnowledgePoints.find(kp => kp.kpId === p.childKpId)
        return point ? point.name : '未知'
      })
      return depNames.join(', ')
    },
    
    async deletePoint(point) {
      if (!this.selectedCourseId) return
      if (confirm('确定要删除这个知识点吗？')) {
        try {
          const token = localStorage.getItem('jwt_token')
          const response = await fetch('/api/v1/knowledge/points/delete', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ kpId: point.kpId })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.fetchKnowledgePoints()
            this.$message.success('知识点删除成功')
          } else if (data.code === 401) {
            this.$message.error('登录已过期，请重新登录')
          } else if (data.code === 403) {
            this.$message.error('权限不足，需要管理员权限才能执行此操作')
          } else {
            this.$message.error(data.message || '删除知识点失败')
          }
        } catch (error) {
          console.error('Failed to delete knowledge point:', error)
          if (error.status === 403) {
            this.$message.error('权限不足，需要管理员权限才能执行此操作')
          } else {
            this.$message.error('删除知识点失败')
          }
        }
      }
    },
    
    async handleSubmit() {
      try {
        const token = localStorage.getItem('jwt_token')
        const payload = {
          ...this.formData,
          kcId: this.selectedCourseId || this.formData.kcId,
          level: parseInt(this.formData.level, 10)
        }
        
        const url = this.showEditModal 
          ? '/api/v1/knowledge/points/update'
          : '/api/v1/knowledge/points/create'
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })
        
        const data = await response.json()
        if (data.code === 200) {
          // 保存知识点关系
          await this.savePointRelations(data.data.kpId)
          
          this.closeModal()
          this.fetchKnowledgePoints()
          this.fetchAllKnowledgePoints()
          this.$message.success(this.showEditModal ? '知识点更新成功' : '知识点创建成功')
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
        } else if (data.code === 403) {
          this.$message.error('权限不足，需要管理员权限才能执行此操作')
        } else {
          this.$message.error(data.message || (this.showEditModal ? '更新知识点失败' : '创建知识点失败'))
        }
      } catch (error) {
        console.error('Failed to save knowledge point:', error)
        if (error.status === 403) {
          this.$message.error('权限不足，需要管理员权限才能执行此操作')
        } else {
          this.$message.error(this.showEditModal ? '更新知识点失败' : '创建知识点失败')
        }
      }
    },
    
    async savePointRelations(kpId) {
      try {
        const token = localStorage.getItem('jwt_token')
        
        // 如果是编辑模式，先删除旧的关系
        if (this.showEditModal) {
          const oldPrereqs = this.allPrerequisites.filter(p => p.childKpId === kpId)
          for (const prereq of oldPrereqs) {
            await fetch('/api/v1/knowledge/prerequisites/delete', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ id: prereq.id })
            })
          }
          
          const oldDeps = this.allPrerequisites.filter(p => p.parentKpId === kpId)
          for (const dep of oldDeps) {
            await fetch('/api/v1/knowledge/prerequisites/delete', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ id: dep.id })
            })
          }
        }
        
        // 保存前向知识点关系
        for (const prereq of this.selectedPrerequisites) {
          await fetch('/api/v1/knowledge/prerequisites/create', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              parentKpId: prereq.kpId,
              childKpId: kpId
            })
          })
        }
        
        // 保存后向知识点关系
        for (const dependent of this.selectedDependents) {
          await fetch('/api/v1/knowledge/prerequisites/create', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              parentKpId: kpId,
              childKpId: dependent.kpId
            })
          })
        }
        
        // 重新加载关系数据
        await this.fetchAllPrerequisites()
      } catch (error) {
        console.error('Failed to save point relations:', error)
        this.$message.warning('知识点关系保存失败，但知识点已保存')
      }
    },
    
    closeModal() {
      this.showCreateModal = false
      this.showEditModal = false
      this.formData = {
        kcId: this.selectedCourseId || 0,
        name: '',
        category: this.selectedCourse?.category || '',
        level: this.selectedCourse?.level || 1,
        description: ''
      }
      this.selectedPrerequisites = []
      this.selectedDependents = []
      this.prerequisiteSearch = ''
      this.dependentSearch = ''
      this.showPrerequisiteDropdown = false
      this.showDependentDropdown = false
    },
    
    closeViewModal() {
      this.showViewModal = false
      this.selectedPoint = {}
      this.selectedPointPrerequisites = []
      this.selectedPointDependents = []
    },
    
    getLevelText(level) {
      const levels = {
        1: '基础',
        2: '进阶',
        3: '高阶'
      }
      return levels[level] || '未知'
    },
    
    truncateDescription(description) {
      if (!description) return '暂无描述'
      return description.length > 50 ? description.substring(0, 50) + '...' : description
    },
    
    formatTime(timestamp) {
      if (!timestamp) return '未知'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('zh-CN')
    },
    
    handleClickOutside(event) {
      // 检查点击是否在下拉框外部
      const prerequisiteDropdowns = this.$el.querySelectorAll('.multi-select-container')
      
      if (this.showPrerequisiteDropdown) {
        const isInsidePrerequisite = Array.from(prerequisiteDropdowns).some((container, index) => {
          // 第一个multi-select-container是前向知识点
          return index === 0 && container.contains(event.target)
        })
        if (!isInsidePrerequisite) {
          this.showPrerequisiteDropdown = false
        }
      }
      
      if (this.showDependentDropdown) {
        const isInsideDependent = Array.from(prerequisiteDropdowns).some((container, index) => {
          // 第二个multi-select-container是后向知识点
          return index === 1 && container.contains(event.target)
        })
        if (!isInsideDependent) {
          this.showDependentDropdown = false
        }
      }
    }
  }
}
</script>

<style scoped>
.knowledge-point-admin {
  padding: 20px;
  max-width: 1800px;
  margin: 0 auto;
}

.course-filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.course-filter-bar label {
  font-weight: 500;
  color: #303133;
  white-space: nowrap;
}

.required-mark {
  color: #f56c6c;
}

.course-filter-bar select {
  min-width: 280px;
  padding: 8px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.course-filter-hint {
  font-size: 13px;
  color: #909399;
}

.selected-course-label {
  margin-right: auto;
  color: #606266;
  font-size: 14px;
}

.empty-course-hint {
  padding: 56px 24px;
  text-align: center;
  color: #909399;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
}

.empty-course-hint__title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 500;
  color: #606266;
}

.empty-course-hint__desc {
  margin: 0;
  font-size: 14px;
}

.search-bar {
  margin-bottom: 20px;
}

.search-bar input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.point-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.point-table th,
.point-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.point-table th {
  background-color: #f5f5f5;
}

.btn-primary,
.btn-secondary,
.btn-view,
.btn-edit,
.btn-delete {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 8px;
}

.btn-primary {
  background-color: #4CAF50;
  color: white;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #333;
}

.btn-view {
  background-color: #FF9800;
  color: white;
}

.btn-edit {
  background-color: #2196F3;
  color: white;
}

.btn-delete {
  background-color: #f44336;
  color: white;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

/* 多选下拉框样式 */
.multi-select-container {
  position: relative;
}

.selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  min-height: 20px;
}

.selected-item {
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.remove-btn {
  background: none;
  border: none;
  color: #2196f3;
  cursor: pointer;
  font-size: 16px;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn:hover {
  color: #f44336;
}

.select-dropdown {
  position: relative;
}

.select-dropdown input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1001;
}

.dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.point-details {
  margin-bottom: 20px;
}

.detail-item {
  margin-bottom: 15px;
  display: flex;
  align-items: flex-start;
}

.detail-item label {
  font-weight: bold;
  min-width: 100px;
  margin-right: 10px;
}

.detail-item span,
.detail-item p {
  flex: 1;
  margin: 0;
}

.detail-item p {
  white-space: pre-wrap;
  line-height: 1.5;
}

/* 关系列表样式 */
.relation-list {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.relation-item {
  background-color: #e8f5e8;
  border: 1px solid #4caf50;
  border-radius: 12px;
  padding: 4px 8px;
  font-size: 12px;
}

.no-relation {
  color: #999;
  font-style: italic;
}

@media (max-width: 768px) {
  .knowledge-point-admin {
    padding: 10px;
  }
  
  .point-table {
    font-size: 12px;
  }
  
  .point-table th,
  .point-table td {
    padding: 8px 4px;
  }
  
  .modal-content {
    width: 95%;
    margin: 10px;
  }
  
  .detail-item {
    flex-direction: column;
  }
  
  .detail-item label {
    min-width: auto;
    margin-bottom: 5px;
  }
  
  .selected-items {
    gap: 4px;
  }
  
  .selected-item {
    font-size: 11px;
    padding: 2px 8px;
  }
}
</style> 